import React, { useState, useEffect, useCallback } from 'react';
import { vdaqApi, assetsApi } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, RefreshCw, Link2, AlertTriangle } from 'lucide-react';

const timeAgo = (iso) => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`;
  return `${Math.floor(ms / 86400000)}d ago`;
};

export const VDaqPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [assets, setAssets] = useState([]);
  const [showMap, setShowMap] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try { const r = await vdaqApi.status(); setStatus(r.data); }
    catch (e) {
      if (e.response?.status === 402) setStatus({ pro_required: true });
      else toast.error('Failed to load V-DAQ status');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const loadMapping = useCallback(async () => {
    try {
      const [v, a] = await Promise.all([vdaqApi.vehicles(), assetsApi.getAll()]);
      setVehicles(v.data || []);
      const list = a.data?.items || a.data || [];
      setAssets(list);
      setShowMap(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load vehicles'); }
  }, []);

  const connect = async () => {
    if (!apiKey.trim()) { toast.error('Paste your V-DAQ API key'); return; }
    setBusy(true);
    try { const r = await vdaqApi.connect(apiKey.trim()); toast.success(`Connected — ${r.data.vehicle_count} vehicle(s) visible`); setApiKey(''); await loadStatus(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not connect'); }
    finally { setBusy(false); }
  };
  const disconnect = async () => { setBusy(true); try { await vdaqApi.disconnect(); toast.success('Disconnected'); setShowMap(false); await loadStatus(); } catch { toast.error('Failed'); } finally { setBusy(false); } };
  const syncNow = async () => { setBusy(true); try { const r = await vdaqApi.sync(); toast.success(`Synced — ${r.data.updated || 0} asset(s) updated`); await loadStatus(); if (showMap) loadMapping(); } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); } finally { setBusy(false); } };

  const linkVehicle = async (vehicle, assetId) => {
    if (!assetId) { await vdaqApi.unlink(vehicle.linked_asset_id).catch(() => {}); loadMapping(); loadStatus(); return; }
    try { await vdaqApi.link({ vehicle_id: vehicle.vehicle_id, asset_id: assetId, registration: vehicle.registration, vin: vehicle.vin }); toast.success(`Linked ${vehicle.registration || vehicle.vehicle_id}`); loadMapping(); loadStatus(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
  };

  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  if (status?.pro_required) return <p className="text-sm text-muted-foreground">V-DAQ telemetry is part of the Pro plan. <a href="/settings?tab=billing" className="text-primary underline">Upgrade</a> to connect your fleet.</p>;

  const connected = status?.connected;
  const statusIcon = !connected ? null : status.status === 'connected' ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />;

  return (
    <div className="flex flex-col gap-4" data-testid="vdaq-panel">
      {status?.simulation && <p className="text-[11px] text-[#8A6D00] bg-[#FFFBEA] border border-[#F2D97A] rounded px-2 py-1 inline-block">SIMULATION MODE — no live V-DAQ endpoint configured; data shown is simulated for testing.</p>}

      {!connected ? (
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">V-DAQ API key</label>
            <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Paste your V-DAQ API key" data-testid="vdaq-key-input" />
          </div>
          <Button onClick={connect} disabled={busy} data-testid="vdaq-connect-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect'}</Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm" data-testid="vdaq-health">
            <div className="rounded-md border border-border p-2"><div className="text-[10px] uppercase text-muted-foreground">Status</div><div className="flex items-center gap-1 font-semibold capitalize">{statusIcon}{status.status}</div></div>
            <div className="rounded-md border border-border p-2"><div className="text-[10px] uppercase text-muted-foreground">Last sync</div><div className="font-semibold">{timeAgo(status.last_sync_at)}</div></div>
            <div className="rounded-md border border-border p-2"><div className="text-[10px] uppercase text-muted-foreground">Linked</div><div className="font-semibold">{status.linked_count} / {status.vehicle_count ?? '—'}</div></div>
            <div className="rounded-md border border-border p-2"><div className="text-[10px] uppercase text-muted-foreground">Silent</div><div className="font-semibold">{status.silent_assets?.length || 0}</div></div>
          </div>
          {status.status_message && <p className="text-xs text-red-600">{status.status_message}</p>}
          {status.silent_assets?.length > 0 && (
            <div className="text-xs text-[#8A6D00] bg-[#FFFBEA] border border-[#F2D97A] rounded px-2 py-1.5 flex items-start gap-1.5" data-testid="vdaq-silent">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>No fresh data from: {status.silent_assets.map((s) => s.name).join(', ')}</span>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={syncNow} disabled={busy} data-testid="vdaq-sync-btn"><RefreshCw className={`w-4 h-4 mr-1 ${busy ? 'animate-spin' : ''}`} />Sync now</Button>
            <Button variant="outline" size="sm" onClick={loadMapping} data-testid="vdaq-map-btn"><Link2 className="w-4 h-4 mr-1" />Map vehicles to assets</Button>
            <Button variant="ghost" size="sm" onClick={disconnect} disabled={busy}>Disconnect</Button>
          </div>

          {showMap && (
            <div className="rounded-md border border-border overflow-hidden" data-testid="vdaq-mapping">
              <div className="px-3 py-2 bg-muted text-xs font-semibold">V-DAQ vehicles → Guardian assets</div>
              {vehicles.length === 0 ? <p className="p-4 text-sm text-muted-foreground">No vehicles visible for this account.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] uppercase text-muted-foreground border-b border-border"><th className="px-3 py-2">Registration</th><th>VIN</th><th className="text-right">Engine hrs</th><th>Linked asset</th></tr></thead>
                  <tbody>
                    {vehicles.map((v, idx) => (
                      <tr key={`${v.vehicle_id}-${idx}`} className={`border-b border-border ${v.linkable === false ? 'opacity-60' : ''}`} data-testid={`vdaq-vehicle-${v.vehicle_id}-${idx}`}>
                        <td className="px-3 py-2 font-mono">
                          {v.registration || <span className="text-muted-foreground">(no reg)</span>}
                          {v.linkable === false && (
                            <span className="ml-2 inline-flex items-center gap-1 rounded border border-[#F2D97A] bg-[#FFFBEA] px-1.5 py-0.5 text-[10px] font-sans font-medium text-[#8A6D00] align-middle" data-testid={`vdaq-vehicle-incomplete-${idx}`}>
                              <AlertTriangle className="w-3 h-3" /> No identifiers
                            </span>
                          )}
                        </td>
                        <td className="font-mono text-xs text-muted-foreground">{v.vin || '—'}</td>
                        <td className="text-right font-mono">{v.engine_hours ?? '—'}</td>
                        <td className="py-1.5">
                          {v.linkable === false ? (
                            <span className="text-xs text-muted-foreground italic" title="This V-DAQ record has no registration or VIN and carries no telemetry, so it can't be mapped to an asset.">Can't link</span>
                          ) : (
                            <select value={v.linked_asset_id || ''} onChange={(e) => linkVehicle(v, e.target.value)} className="h-8 rounded-md border border-border px-2 text-sm w-full max-w-[220px]" data-testid={`vdaq-link-${v.vehicle_id}-${idx}`}>
                              <option value="">— Not linked —</option>
                              {assets.map((a) => <option key={a.id} value={a.id}>{a.unit_number || a.name}</option>)}
                            </select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VDaqPanel;
